import type { DestroyRef, WritableSignal } from '@angular/core';
import type { FormGroup } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { finalize } from 'rxjs';
import type { SpeciesDto } from 'projects/cbd-proxies/src/lib/general/dtos/species/models';
import type { GeneticMaterialSelectOption } from '../stand-creation-form/services/stand-catalogs';
import { GeneticMaterialLoader } from '../stand-creation-form/types/plantation-types';
import { SyncSpeciesWithGeneticMaterialParams } from '../stand-creation-form/interfaces/ISyncSpeciesWithGeneticMaterialFormParams';

export function resolveSpeciesFromGeneticMaterial(
  geneticMaterialId: string | number | null | undefined,
  geneticMaterialOptions: GeneticMaterialSelectOption[],
): SpeciesDto | null {
  if (geneticMaterialId === null || geneticMaterialId === undefined || geneticMaterialId === '') {
    return null;
  }

  return (
    geneticMaterialOptions.find(option => String(option.key) === String(geneticMaterialId))
      ?.species ?? null
  );
}

interface SyncSpeciesWithGeneticMaterialFormParams {
  destroyRef: DestroyRef;
  form: FormGroup;
  geneticMaterialOptions: WritableSignal<GeneticMaterialSelectOption[]>;
  geneticMaterialsLoader: GeneticMaterialLoader;
  applySpeciesSelection: (species: SpeciesDto | null) => void;
  loading: WritableSignal<boolean>;
  pendingRequestIds?: Set<string>;
}

export function syncSpeciesWithGeneticMaterial({
  destroyRef,
  geneticMaterialId,
  geneticMaterialOptions,
  geneticMaterialsLoader,
  updateGeneticMaterialOptions,
  applySpeciesSelection,
  setLoading,
  isCurrentGeneticMaterialSelected,
  pendingRequestIds,
}: SyncSpeciesWithGeneticMaterialParams): void {
  if (!geneticMaterialId) {
    applySpeciesSelection(null);
    return;
  }

  const geneticMaterialIdAsString = String(geneticMaterialId);
  const selectedSpecies = resolveSpeciesFromGeneticMaterial(
    geneticMaterialIdAsString,
    geneticMaterialOptions,
  );

  if (selectedSpecies || geneticMaterialOptions.some(
    option => String(option.key) === geneticMaterialIdAsString,
  )) {
    applySpeciesSelection(selectedSpecies);
    return;
  }

  if (pendingRequestIds?.has(geneticMaterialIdAsString)) {
    return;
  }

  applySpeciesSelection(null);
  pendingRequestIds?.add(geneticMaterialIdAsString);
  setLoading(true);

  geneticMaterialsLoader({ ids: [geneticMaterialIdAsString] })
    .pipe(
      takeUntilDestroyed(destroyRef),
      finalize(() => {
        pendingRequestIds?.delete(geneticMaterialIdAsString);
        setLoading((pendingRequestIds?.size ?? 0) > 0);
      }),
    )
    .subscribe({
      next: result => {
        const selectedGeneticMaterial = result.items[0];

        if (!selectedGeneticMaterial) {
          return;
        }

        if (
          isCurrentGeneticMaterialSelected &&
          !isCurrentGeneticMaterialSelected(geneticMaterialIdAsString)
        ) {
          return;
        }

        updateGeneticMaterialOptions(current =>
          current.some(option => String(option.key) === geneticMaterialIdAsString)
            ? current
            : [...current, selectedGeneticMaterial],
        );
        applySpeciesSelection(selectedGeneticMaterial.species ?? null);
      },
    });
}

export function syncSpeciesWithGeneticMaterialFromForm({
  destroyRef,
  form,
  geneticMaterialOptions,
  geneticMaterialsLoader,
  applySpeciesSelection,
  loading,
  pendingRequestIds,
}: SyncSpeciesWithGeneticMaterialFormParams): void {
  const geneticMaterialControl = form.get('GeneticMaterial');

  syncSpeciesWithGeneticMaterial({
    destroyRef,
    geneticMaterialId: geneticMaterialControl?.value,
    geneticMaterialOptions: geneticMaterialOptions(),
    geneticMaterialsLoader,
    updateGeneticMaterialOptions: updater => geneticMaterialOptions.update(updater),
    applySpeciesSelection,
    setLoading: isLoading => loading.set(isLoading),
    isCurrentGeneticMaterialSelected: geneticMaterialId =>
      String(geneticMaterialControl?.value) === geneticMaterialId,
    pendingRequestIds,
  });
}
